/**
 * Phase C public-by-address miner API client.
 *
 * Hits https://gonkahub.com/api/public/* directly (CORS is already
 * allowed for wallet.gonkahub.com via the backend's regex). No auth
 * header, no cookies. Cross-origin plain fetch.
 */

const MINER_API_BASE = 'https://gonkahub.com/api/public';

export interface MinerStats {
  readonly address: string;
  readonly is_active: boolean;
  readonly rank: number | null;
  readonly total_participants: number;
  readonly weight: number;
  readonly gpus: number;
  readonly share: number;
  readonly missed_percent: number;
  readonly invalid_percent: number;
  readonly is_healthy: boolean | null;
  readonly is_jailed: boolean | null;
  readonly participant_status: string | null;
  readonly weight_to_confirm: number | null;
  readonly confirmation_ratio: string | null;
  readonly models: readonly string[];
  readonly moniker: string | null;
  readonly inference_count: number;
  readonly missed_requests: number;
  readonly validated_inferences: number;
  readonly invalidated_inferences: number;
  readonly has_collateral: boolean | null;
  readonly collateral_is_sufficient: boolean | null;
}

export interface MinerStatsResponse {
  readonly miner: MinerStats;
}

export interface HardwareGpu {
  readonly type: string;
  readonly count: number;
}

export interface HardwareNode {
  readonly local_id: string;
  readonly status: string;
  readonly models?: readonly string[];
  readonly hardware?: readonly HardwareGpu[];
  readonly host?: string;
  readonly port?: string;
  readonly poc_weight?: number;
}

export interface HardwareResponse {
  readonly address: string;
  readonly hardware_nodes: readonly HardwareNode[];
}

export interface RewardEntry {
  readonly epoch_id: number;
  readonly assigned_reward: number;
  readonly weight: number | null;
  readonly claimed: boolean;
  readonly duration: string | null;
  readonly duration_change_percent: number | null;
}

export interface RewardsResponse {
  readonly address: string;
  readonly total_rewards: number;
  readonly rewards: readonly RewardEntry[];
  readonly pagination: {
    readonly page: number;
    readonly total_pages: number;
    readonly total_count: number;
    readonly per_page: number;
    readonly has_prev: boolean;
    readonly has_next: boolean;
  };
}

export interface EpochInfo {
  readonly epoch_id: number;
  readonly block_height: number;
  readonly epoch_timing: {
    readonly hours_remaining?: number;
    readonly epoch_progress?: number;
    readonly estimated_end?: string;
    readonly epoch_phase?: string;
    readonly seconds_remaining?: number;
  };
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${MINER_API_BASE}${path}`, {
    method: 'GET',
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`miner-api ${res.status} ${res.statusText} on ${path}`);
  }
  return (await res.json()) as T;
}

export function fetchMinerStats(address: string): Promise<MinerStatsResponse> {
  return getJson<MinerStatsResponse>(`/miner-stats/${address}/`);
}

export function fetchMinerHardware(address: string): Promise<HardwareResponse> {
  return getJson<HardwareResponse>(`/miner-hardware/${address}/`);
}

export function fetchMinerRewards(
  address: string,
  page = 1,
): Promise<RewardsResponse> {
  return getJson<RewardsResponse>(`/miner-rewards/${address}/?page=${page}`);
}

export function fetchEpoch(): Promise<EpochInfo> {
  return getJson<EpochInfo>(`/epoch/`);
}

// ────────────────────────────────────────────────────────────────────
// Phase D: transactions + reward nodes + collateral
// ────────────────────────────────────────────────────────────────────

export interface TransactionMessage {
  readonly type: string;
  readonly from?: string;
  readonly to?: string;
  readonly amount?: string;
  readonly denom?: string;
}

export interface Transaction {
  readonly hash: string;
  readonly height: number;
  readonly timestamp: string;
  readonly status: string;
  readonly direction: 'sent' | 'received' | 'self' | 'unknown';
  readonly type: string;
  readonly type_display?: {
    readonly en: string;
    readonly ru?: string;
    readonly icon?: string;
  };
  readonly messages: readonly TransactionMessage[];
  readonly messages_count: number;
  readonly amount: string;
  readonly amount_gnk: number;
  readonly denom: string;
  readonly fee_gnk: number;
  readonly gas_used: number;
  readonly gas_wanted: number;
  readonly memo?: string;
}

export interface TransactionsResponse {
  readonly address: string;
  readonly page: number;
  readonly limit: number;
  readonly filter: 'all' | 'sent' | 'received';
  readonly transactions: readonly Transaction[];
  readonly total_count: number;
  readonly has_more: boolean;
}

export interface RewardNode {
  readonly local_id: string;
  readonly poc_weight: number;
  readonly reward_gnk: string;
  readonly reward_ugnk: number;
  readonly weight_percent: string;
}

export interface MinerRewardNodesResponse {
  readonly epoch_id: number;
  readonly participant_address: string;
  readonly total_reward_gnk: string;
  readonly total_reward_ugnk: number;
  readonly nodes: readonly RewardNode[];
  readonly has_breakdown: boolean;
  readonly nodes_count: number;
  readonly epoch_start: string | null;
  readonly epoch_end: string | null;
}

export interface CollateralHistoryItem {
  readonly epoch_id: number;
  readonly deposited_gnk: number;
  readonly required_gnk: number;
  readonly has_collateral: boolean;
  readonly coverage_pct: number;
  readonly weight: number;
  readonly slashed_gnk: number;
}

export interface CollateralData {
  readonly address: string;
  readonly deposited_gnk: number;
  readonly required_gnk: number;
  readonly weight: number;
  readonly has_collateral: boolean;
  readonly is_sufficient: boolean;
  readonly base_weight: number;
  readonly eligible_weight: number;
  readonly activated_weight: number;
  readonly effective_weight: number;
  readonly weight_efficiency_pct: number;
  readonly collateral_ratio: number;
  readonly collateral_per_weight_unit_ngonka: number;
  readonly grace_period_end_epoch: number;
  readonly grace_period_active: boolean;
  readonly unbonding_period_epochs: number;
  readonly slash_fraction_invalid: number;
  readonly slash_fraction_downtime: number;
  readonly downtime_missed_percentage_threshold: number;
  readonly history: readonly CollateralHistoryItem[];
}

export function fetchTransactions(
  address: string,
  page = 1,
  filter: 'all' | 'sent' | 'received' = 'all',
): Promise<TransactionsResponse> {
  return getJson<TransactionsResponse>(
    `/transactions/${address}/?page=${page}&filter=${filter}`,
  );
}

export function fetchMinerRewardNodes(
  address: string,
  epochId: number,
): Promise<MinerRewardNodesResponse> {
  return getJson<MinerRewardNodesResponse>(
    `/miner-rewards/${address}/${epochId}/nodes/`,
  );
}

export function fetchMinerCollateral(address: string): Promise<CollateralData> {
  return getJson<CollateralData>(`/miner-collateral/${address}/`);
}

export interface VestingResponse {
  readonly address: string;
  readonly has_vesting: boolean;
  readonly total: number;
  readonly locked: number;
  readonly unlocked: number;
  readonly total_ngonka: string;
  readonly locked_ngonka: string;
  readonly unlocked_ngonka: string;
}

export function fetchVesting(address: string): Promise<VestingResponse> {
  return getJson<VestingResponse>(`/vesting/${address}/`);
}
